import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, ChevronRight, Calendar, Plus, X,
  Mic2, Users, Flag, Star, Globe, PenLine,
  CheckCircle2, Sparkles, MapPin,
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const POSTURE_COLORS = {
  speak: "bg-violet-500",
  attend: "bg-blue-500",
  sponsor: "bg-amber-500",
  outreach_only: "bg-teal-500",
};

const MARKER_COLORS = {
  public_holiday: "bg-red-100 text-red-700 border-red-200",
  national_day: "bg-amber-100 text-amber-800 border-amber-200",
  pan_african: "bg-emerald-100 text-emerald-700 border-emerald-200",
  international: "bg-sky-100 text-sky-700 border-sky-200",
  observance: "bg-rose-100 text-rose-700 border-rose-200",
  cultural: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  custom: "bg-purple-100 text-purple-700 border-purple-200",
  scheduled_topic: "bg-violet-100 text-violet-700 border-violet-200",
  company_milestone: "bg-indigo-100 text-indigo-700 border-indigo-200",
  client_event: "bg-teal-100 text-teal-700 border-teal-200",
};

const COUNTRY_FLAGS = {
  Nigeria: "\u{1F1F3}\u{1F1EC}",
  Ghana: "\u{1F1EC}\u{1F1ED}",
  Kenya: "\u{1F1F0}\u{1F1EA}",
  "South Africa": "\u{1F1FF}\u{1F1E6}",
  "Pan-African": "\u{1F30D}",
};

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month - 1, 1).getDay();
  return d === 0 ? 6 : d - 1; // Convert to Mon=0
}

function isToday(year, month, day) {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() + 1 === month && now.getDate() === day;
}

export function ContentCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAddMarker, setShowAddMarker] = useState(false);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/content-calendar?month=${month}&year=${year}`, { credentials: "include" });
      if (res.ok) {
        setCalendarData(await res.json());
      }
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const goToPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else { setMonth(m => m - 1); }
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else { setMonth(m => m + 1); }
    setSelectedDay(null);
  };

  const goToToday = () => {
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
    setSelectedDay(null);
  };

  const items = calendarData?.items || {};
  const summary = calendarData?.summary || {};
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Build grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const getDayKey = (day) => `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const getDayItems = (day) => items[getDayKey(day)] || { events: [], drafts: [], markers: [] };

  const selectedDayItems = selectedDay ? getDayItems(selectedDay) : null;
  const selectedDayKey = selectedDay ? getDayKey(selectedDay) : null;

  return (
    <div className="flex h-full" data-testid="content-calendar">
      {/* Calendar Grid */}
      <div className={`${selectedDay ? "w-2/3" : "w-full"} transition-all flex flex-col`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={goToPrevMonth} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="cal-prev-month">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <h2 className="text-base font-bold text-slate-900 min-w-[160px] text-center" data-testid="cal-month-label">
              {MONTHS[month - 1]} {year}
            </h2>
            <button onClick={goToNextMonth} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="cal-next-month">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
            <Button variant="outline" size="sm" onClick={goToToday} className="text-xs ml-2" data-testid="cal-today-btn">
              Today
            </Button>
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            {summary.committed_events > 0 && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> {summary.committed_events} events</span>
            )}
            {summary.scheduled_drafts > 0 && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> {summary.scheduled_drafts} posts</span>
            )}
            {summary.holidays > 0 && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {summary.holidays} holidays</span>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowAddMarker(true)} className="text-xs" data-testid="add-custom-date-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Date
            </Button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAYS.map(d => (
            <div key={d} className="px-2 py-2 text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="flex-1 grid grid-cols-7 auto-rows-fr overflow-y-auto">
          {cells.map((day, i) => {
            if (day === null) {
              return <div key={`empty-${i}`} className="border-b border-r border-slate-100 bg-slate-50/50" />;
            }
            const dayItems = getDayItems(day);
            const hasContent = dayItems.events.length > 0 || dayItems.drafts.length > 0 || dayItems.markers.length > 0;
            const today = isToday(year, month, day);
            const selected = selectedDay === day;

            return (
              <div
                key={day}
                onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                className={`border-b border-r border-slate-100 p-1 cursor-pointer transition-colors min-h-[80px] ${
                  today ? "bg-blue-50/60" : selected ? "bg-slate-50" : "hover:bg-slate-50/50"
                } ${selected ? "ring-2 ring-blue-400 ring-inset" : ""}`}
                data-testid={`cal-day-${day}`}
              >
                {/* Day number */}
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-xs font-medium ${
                    today ? "w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center" : "text-slate-600 pl-0.5"
                  }`}>
                    {day}
                  </span>
                  {hasContent && (
                    <div className="flex gap-0.5">
                      {dayItems.events.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                      {dayItems.drafts.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />}
                      {dayItems.markers.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    </div>
                  )}
                </div>

                {/* Content chips */}
                <div className="space-y-0.5">
                  {dayItems.markers.slice(0, 2).map((m, j) => (
                    <div key={j} className={`text-[8px] leading-tight px-1 py-0.5 rounded truncate border ${MARKER_COLORS[m.type] || MARKER_COLORS.custom}`}>
                      {m.title}
                    </div>
                  ))}
                  {dayItems.events.slice(0, 1).map((e, j) => (
                    <div key={j} className="text-[8px] leading-tight px-1 py-0.5 rounded truncate bg-blue-100 text-blue-700 border border-blue-200">
                      <Users className="w-2 h-2 inline mr-0.5" />{e.name?.substring(0, 25)}
                    </div>
                  ))}
                  {dayItems.drafts.slice(0, 1).map((d, j) => (
                    <div key={j} className="text-[8px] leading-tight px-1 py-0.5 rounded truncate bg-violet-100 text-violet-700 border border-violet-200">
                      <PenLine className="w-2 h-2 inline mr-0.5" />{d.topic?.substring(0, 25)}
                    </div>
                  ))}
                  {(dayItems.markers.length + dayItems.events.length + dayItems.drafts.length) > 3 && (
                    <div className="text-[8px] text-slate-400 pl-1">
                      +{dayItems.markers.length + dayItems.events.length + dayItems.drafts.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="px-6 py-2 border-t border-slate-200 flex items-center gap-4 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Committed Event</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> Scheduled Post</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Holiday / National Day</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /> Custom Date</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Content Opportunity</span>
        </div>
      </div>

      {/* Day Detail Panel */}
      {selectedDay && (
        <div className="w-1/3 border-l border-slate-200 bg-white overflow-y-auto">
          <DayDetail
            day={selectedDay}
            month={month}
            year={year}
            items={selectedDayItems}
            dateKey={selectedDayKey}
            onClose={() => setSelectedDay(null)}
            onRefresh={fetchCalendar}
          />
        </div>
      )}

      {/* Add Custom Date Modal */}
      {showAddMarker && (
        <AddMarkerModal
          onClose={() => setShowAddMarker(false)}
          onSaved={() => { setShowAddMarker(false); fetchCalendar(); }}
          defaultDate={selectedDay ? getDayKey(selectedDay) : ""}
        />
      )}
    </div>
  );
}

// ─── Day Detail Panel ───────────────────────────────────────────

function DayDetail({ day, month, year, items, dateKey, onClose, onRefresh }) {
  const dayName = new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const hasAnything = items.events.length + items.drafts.length + items.markers.length > 0;

  return (
    <div className="flex flex-col h-full" data-testid="day-detail-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800">{dayName}</h3>
          <p className="text-[10px] text-slate-500">
            {items.events.length} events, {items.drafts.length} posts, {items.markers.length} markers
          </p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* Holidays & Markers */}
        {items.markers.length > 0 && (
          <section>
            <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Flag className="w-3 h-3" /> Holidays & Dates
            </h4>
            <div className="space-y-2">
              {items.markers.map((m, i) => (
                <div key={i} className={`p-3 rounded-lg border ${MARKER_COLORS[m.type] || MARKER_COLORS.custom}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{COUNTRY_FLAGS[m.country] || ""}</span>
                    <div className="flex-1">
                      <p className="text-xs font-semibold">{m.title}</p>
                      <p className="text-[10px] opacity-70">{m.country} {m.type === "custom" ? "- Custom" : ""}</p>
                    </div>
                    {m.content_opportunity && (
                      <Badge variant="outline" className="text-[8px] border-amber-300 text-amber-600 bg-amber-50">
                        <Sparkles className="w-2 h-2 mr-0.5" /> Content Opp
                      </Badge>
                    )}
                  </div>
                  {m.notes && <p className="text-[10px] mt-1 opacity-70">{m.notes}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Committed Events */}
        {items.events.length > 0 && (
          <section>
            <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> Committed Events
            </h4>
            <div className="space-y-2">
              {items.events.map((e, i) => (
                <div key={i} className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                  <div className="flex items-start gap-2">
                    <span className={`w-2 h-2 rounded-full mt-1 ${POSTURE_COLORS[e.our_posture] || "bg-blue-500"}`} />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-blue-800">{e.name}</p>
                      <p className="text-[10px] text-blue-600">
                        <MapPin className="w-2.5 h-2.5 inline" /> {e.city}, {e.country}
                      </p>
                      <Badge variant="outline" className="text-[8px] mt-1 border-blue-300 text-blue-600 capitalize">
                        {e.our_posture?.replace("_", " ")}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Scheduled Drafts */}
        {items.drafts.length > 0 && (
          <section>
            <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <PenLine className="w-3 h-3" /> Scheduled Posts
            </h4>
            <div className="space-y-2">
              {items.drafts.map((d, i) => (
                <div key={i} className="p-3 rounded-lg border border-violet-200 bg-violet-50">
                  <p className="text-xs font-semibold text-violet-800">{d.topic}</p>
                  {d.content && (
                    <p className="text-[10px] text-violet-600 mt-1 line-clamp-3">{d.content.substring(0, 150)}...</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-[8px] capitalize border-violet-300 text-violet-600">{d.status}</Badge>
                    <Badge variant="outline" className="text-[8px] capitalize border-violet-300 text-violet-600">{d.post_type}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {!hasAnything && (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Nothing scheduled for this day</p>
            <p className="text-[10px] text-slate-300 mt-1">Events you commit to and scheduled posts will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Add Custom Marker Modal ────────────────────────────────────

function AddMarkerModal({ onClose, onSaved, defaultDate }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate || "");
  const [country, setCountry] = useState("");
  const [type, setType] = useState("custom");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title || !date) {
      toast.error("Title and date are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/content-calendar/markers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, date, country, type, notes, content_opportunity: true }),
      });
      if (res.ok) {
        toast.success("Date added to calendar");
        onSaved();
      } else {
        toast.error("Failed to add date");
      }
    } catch {
      toast.error("Failed to add date");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="add-marker-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-800">Add Custom Date</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Title *</label>
            <Input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Company Anniversary, Client Milestone"
              data-testid="marker-title"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Date *</label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="marker-date" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Country / Region</label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger><SelectValue placeholder="Select country (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Nigeria">Nigeria</SelectItem>
                <SelectItem value="Ghana">Ghana</SelectItem>
                <SelectItem value="Kenya">Kenya</SelectItem>
                <SelectItem value="South Africa">South Africa</SelectItem>
                <SelectItem value="Pan-African">Pan-African</SelectItem>
                <SelectItem value="RESILIGENT">RESILIGENT (Internal)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom Date</SelectItem>
                <SelectItem value="national_day">National Day</SelectItem>
                <SelectItem value="public_holiday">Public Holiday</SelectItem>
                <SelectItem value="company_milestone">Company Milestone</SelectItem>
                <SelectItem value="client_event">Client Event</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." data-testid="marker-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-marker-btn">
            {saving ? "Saving..." : "Add to Calendar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
